import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Chip, Tabs, Tab, Button, Checkbox, CircularProgress, Pagination,
  Alert, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Tooltip, Stack, TextField, MenuItem,
  FormControlLabel,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { dictionaryService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import Suggestions from './Suggestions';

const FIELD_LABEL = {
  grammar: 'Grammaire', french: 'Français', english: 'Anglais', spanish: 'Espagnol', german: 'Allemand',
  note: 'Note', note_english: 'Note (anglais)', note_spanish: 'Note (espagnol)', note_german: 'Note (allemand)',
  examples: 'Exemples', creation: 'Nouveau mot',
};
const SOURCE_LABEL = { direct: 'Modification directe', proposition: 'Proposition acceptée', annulation: 'Annulation' };
const PAGE_SIZE = 50;

const parseJson = (v) => { if (typeof v !== 'string') return v; try { return JSON.parse(v); } catch (_) { return v; } };

// Texte lisible d'une valeur de champ (les exemples sont une liste d'objets)
const show = (field, value) => {
  const v = parseJson(value);
  if (v === null || v === undefined || v === '') return '(vide)';
  if (field === 'creation' && v && typeof v === 'object') {
    return ['suggestion', 'grammar', 'french', 'english', 'spanish', 'note'].filter((k) => v[k]).map((k) => `${k === 'suggestion' ? 'Suggestion' : (FIELD_LABEL[k] || k)} : ${v[k]}`).join('\n');
  }
  if (field === 'examples' && Array.isArray(v)) {
    return v.map((ex, i) => `${i + 1}. ${ex.duala || ''}${ex.francais ? ' — ' + ex.francais : ''}`).join('\n');
  }
  return String(v);
};

const personName = (u) => (u ? (`${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email) : '-');

function Box2({ title, text, color }) {
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary">{title}</Typography>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', borderLeft: 3, borderColor: color, pl: 1 }}>
        {text}
      </Typography>
    </Box>
  );
}

// Relecture par lettre dans Excel : export d'un classeur par lettre, import du classeur corrige
// (chaque cellule changee devient une proposition, rien n'est ecrit directement).
function ExcelTab({ isSuperAdmin }) {
  const { enqueueSnackbar } = useSnackbar();
  const [letters, setLetters] = useState([]);
  const [letter, setLetter] = useState('');
  const [unverified, setUnverified] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [file, setFile] = useState(null);
  const [reviewer, setReviewer] = useState('');
  const [importing, setImporting] = useState(false);
  const [report, setReport] = useState(null);

  useEffect(() => {
    dictionaryService.getLetters()
      .then((res) => setLetters((res.data.letters || []).map((l) => (typeof l === 'string' ? l : l.letter)).filter(Boolean)))
      .catch(() => {});
  }, []);

  const doExport = async () => {
    setExporting(true);
    try {
      const res = await dictionaryService.exportExcel(letter, unverified);
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dictionnaire-${letter || 'tout'}${unverified ? '-a-verifier' : ''}-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      enqueueSnackbar('Erreur lors de l\'export', { variant: 'error' });
    } finally {
      setExporting(false);
    }
  };

  const doImport = async () => {
    setImporting(true);
    setReport(null);
    try {
      const res = await dictionaryService.importExcel(file, reviewer);
      setReport(res.data.report);
      enqueueSnackbar(`${res.data.report.created} proposition(s) créée(s)`, { variant: 'success' });
      setFile(null);
    } catch (err) {
      enqueueSnackbar((err.response && err.response.data && err.response.data.error) || 'Erreur lors de l\'import', { variant: 'error' });
    } finally {
      setImporting(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>1. Envoyer un fichier à un relecteur</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Un classeur Excel par lettre, avec le mode d&apos;emploi à l&apos;intérieur. Le relecteur corrige les cellules et vous le renvoie.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <TextField select size="small" label="Lettre" value={letter} onChange={(e) => setLetter(e.target.value)} sx={{ minWidth: 140 }}>
              <MenuItem value="">Toutes les lettres</MenuItem>
              {letters.map((l) => <MenuItem key={l} value={l}>{l}</MenuItem>)}
            </TextField>
            <FormControlLabel control={<Checkbox checked={unverified} onChange={(e) => setUnverified(e.target.checked)} />} label="Seulement les mots non vérifiés" />
            <Button variant="contained" onClick={doExport} disabled={exporting}>{exporting ? 'Préparation...' : 'Télécharger le classeur'}</Button>
          </Stack>
        </CardContent>
      </Card>

      {isSuperAdmin && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>2. Récupérer le fichier corrigé</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Seules les cellules réellement changées deviennent des propositions, à valider dans l&apos;onglet « À valider ». Le dictionnaire n&apos;est pas modifié par l&apos;import.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
              <TextField size="small" label="Nom du relecteur (facultatif)" value={reviewer} onChange={(e) => setReviewer(e.target.value)} />
              <Button component="label" variant="outlined">
                {file ? file.name : 'Choisir le fichier .xlsx'}
                <input type="file" hidden accept=".xlsx" onChange={(e) => { setFile(e.target.files[0] || null); setReport(null); }} />
              </Button>
              <Button variant="contained" onClick={doImport} disabled={!file || importing}>{importing ? 'Import...' : 'Importer'}</Button>
            </Stack>

            {report && (
              <Alert severity={report.errors.length ? 'warning' : 'success'} sx={{ mt: 2 }}>
                {report.rows} ligne(s) lues, {report.changedFields} champ(s) changé(s) : <strong>{report.created} proposition(s) créée(s)</strong>
                {report.alreadyUpToDate ? `, ${report.alreadyUpToDate} déjà à jour` : ''}
                {report.duplicates ? `, ${report.duplicates} déjà proposée(s)` : ''}.
                {report.errors.length > 0 && (
                  <Box component="ul" sx={{ m: 0, mt: 1, pl: 3 }}>
                    {report.errors.slice(0, 20).map((e, i) => <li key={i}><em>{e.duala}</em> : {e.error}</li>)}
                    {report.errors.length > 20 && <li>… et {report.errors.length - 20} autre(s)</li>}
                  </Box>
                )}
              </Alert>
            )}
          </CardContent>
        </Card>
      )}
    </Stack>
  );
}

function Validation() {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const isSuperAdmin = user && user.role === 'super_admin';

  const [tab, setTab] = useState(0);

  // --- propositions
  const [proposals, setProposals] = useState([]);
  const [counts, setCounts] = useState({ nouveau: 0 });
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('nouveau');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [lastReport, setLastReport] = useState(null);
  const [legacyCount, setLegacyCount] = useState(0);

  // --- historique
  const [changes, setChanges] = useState([]);
  const [changesTotal, setChangesTotal] = useState(0);
  const [changesPage, setChangesPage] = useState(1);
  const [loadingChanges, setLoadingChanges] = useState(false);

  const loadProposals = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dictionaryService.getProposals({ status, page, limit: PAGE_SIZE });
      setProposals(res.data.proposals);
      setTotal(res.data.total);
      setCounts(res.data.counts);
      setSelected([]);
    } catch (err) {
      console.error('Erreur chargement propositions:', err);
      enqueueSnackbar('Erreur lors du chargement des propositions', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [status, page, enqueueSnackbar]);

  const loadChanges = useCallback(async () => {
    setLoadingChanges(true);
    try {
      const res = await dictionaryService.getChanges({ page: changesPage, limit: PAGE_SIZE });
      setChanges(res.data.changes);
      setChangesTotal(res.data.total);
    } catch (err) {
      console.error('Erreur chargement historique:', err);
      enqueueSnackbar('Erreur lors du chargement de l\'historique', { variant: 'error' });
    } finally {
      setLoadingChanges(false);
    }
  }, [changesPage, enqueueSnackbar]);

  // anciens signalements en texte libre (avant les propositions structurees), traites dans « Suggestions mots »
  useEffect(() => {
    dictionaryService.getCorrections({ status: 'nouveau', limit: 1 })
      .then((res) => setLegacyCount(res.data.total || 0))
      .catch(() => {});
  }, []);

  useEffect(() => { loadProposals(); }, [loadProposals]);
  useEffect(() => { if (tab === 3) loadChanges(); }, [tab, loadChanges]);

  const toggle = (id) => setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  // « sûres » : texte inchangé depuis la proposition et aucune alerte de caractère
  const safeIds = proposals.filter((p) => p.status === 'nouveau' && !p.stale && !p.missing && p.warnings.length === 0).map((p) => p.id);

  const accept = async (ids, force = false) => {
    if (ids.length === 0) return;
    setBusy(true);
    try {
      const res = await dictionaryService.acceptProposals(ids, force);
      setLastReport(res.data);
      const failed = res.data.report.filter((r) => !r.ok);
      enqueueSnackbar(`${res.data.accepted} proposition(s) appliquée(s)${failed.length ? `, ${failed.length} non appliquée(s)` : ''}`,
        { variant: failed.length ? 'warning' : 'success' });
      await loadProposals();
    } catch (err) {
      const message = err.response && err.response.data && err.response.data.error;
      enqueueSnackbar(message || 'Erreur lors de l\'application', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const refuse = async (ids) => {
    if (ids.length === 0) return;
    setBusy(true);
    try {
      const res = await dictionaryService.refuseProposals(ids);
      enqueueSnackbar(`${res.data.refused} proposition(s) refusée(s)`, { variant: 'info' });
      await loadProposals();
    } catch (err) {
      enqueueSnackbar('Erreur lors du refus', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const revert = async (change, force = false) => {
    if (!force && !window.confirm(`Annuler cette modification de « ${change.duala} » (${FIELD_LABEL[change.field] || change.field}) ?`)) return;
    try {
      await dictionaryService.revertChange(change.id, force);
      enqueueSnackbar('Modification annulée', { variant: 'success' });
      loadChanges();
    } catch (err) {
      const data = err.response && err.response.data;
      if (data && data.stale && window.confirm(`${data.error}.\n\nRemettre quand même l'ancienne valeur ?`)) {
        revert(change, true);
        return;
      }
      enqueueSnackbar((data && data.error) || 'Erreur lors de l\'annulation', { variant: 'error' });
    }
  };

  const failedReport = lastReport ? lastReport.report.filter((r) => !r.ok) : [];
  const staleFailed = failedReport.filter((r) => r.stale).map((r) => r.id);

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h5">Corrections du dictionnaire</Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
        <Tab label={`À valider (${counts.nouveau || 0})`} />
        <Tab label={legacyCount > 0 ? `Anciens signalements (${legacyCount})` : 'Anciens signalements'} />
        <Tab label="Mots suggérés" />
        <Tab label="Historique" />
        <Tab label="Relecture Excel" />
      </Tabs>

      {tab === 0 && (
        <Card>
          <CardContent>
            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }} alignItems="center">
              {[['nouveau', 'À valider'], ['accepte', 'Acceptées'], ['refuse', 'Refusées']].map(([key, label]) => (
                <Chip key={key} label={`${label} (${counts[key] || 0})`} color={status === key ? 'primary' : 'default'}
                  onClick={() => { setStatus(key); setPage(1); }} />
              ))}
              {isSuperAdmin && status === 'nouveau' && (
                <>
                  <Box sx={{ flex: 1 }} />
                  <Button size="small" onClick={() => setSelected(safeIds)} disabled={safeIds.length === 0 || busy}>
                    Sélectionner les propositions sûres ({safeIds.length})
                  </Button>
                  <Button size="small" variant="contained" onClick={() => accept(selected)} disabled={selected.length === 0 || busy}>
                    Accepter la sélection ({selected.length})
                  </Button>
                  <Button size="small" color="error" onClick={() => refuse(selected)} disabled={selected.length === 0 || busy}>
                    Refuser la sélection
                  </Button>
                </>
              )}
            </Stack>

            {isSuperAdmin && status === 'nouveau' && (
              <Alert severity="info" sx={{ mb: 2 }}>
                « Sûre » = le texte du dictionnaire n&apos;a pas changé depuis la proposition et aucun caractère suspect n&apos;a été détecté.
                Avant une grosse série, faites une sauvegarde de la base ; chaque modification reste annulable dans l&apos;onglet Historique.
              </Alert>
            )}

            {failedReport.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }} action={staleFailed.length > 0 && (
                <Button color="inherit" size="small" onClick={() => accept(staleFailed, true)} disabled={busy}>
                  Appliquer quand même ({staleFailed.length})
                </Button>
              )}>
                {failedReport.length} proposition(s) non appliquée(s) :{' '}
                {failedReport.slice(0, 8).map((r) => `${r.duala} (${r.reason})`).join(' ; ')}
                {failedReport.length > 8 ? '…' : ''}
              </Alert>
            )}

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
            ) : proposals.length === 0 ? (
              <Typography color="text.secondary">Aucune proposition dans cette liste.</Typography>
            ) : (
              <Stack spacing={1.5}>
                {proposals.map((p) => (
                  <Paper key={p.id} variant="outlined" sx={{ p: 1.5, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    {isSuperAdmin && status === 'nouveau' && (
                      <Checkbox checked={selected.includes(p.id)} onChange={() => toggle(p.id)} disabled={busy || p.missing} />
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5, mb: 1 }}>
                        <Typography sx={{ fontWeight: 700, fontStyle: 'italic' }}>{p.duala}</Typography>
                        <Chip size="small" label={FIELD_LABEL[p.field] || p.field} />
                        <Typography variant="caption" color="text.secondary">
                          par {personName(p.submitter)} · {new Date(p.created_at).toLocaleDateString('fr-FR')}
                        </Typography>
                        {p.stale && <Tooltip title="Le texte du dictionnaire a été modifié depuis cette proposition"><Chip size="small" color="warning" label="Texte modifié depuis" /></Tooltip>}
                        {p.missing && <Chip size="small" color="error" label="Mot introuvable" />}
                        {p.warnings.map((w) => <Chip key={w} size="small" color="error" label={w} />)}
                      </Stack>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <Box2 title={p.stale ? 'Dans le dictionnaire maintenant' : 'Actuel'} text={show(p.field, p.current)} color="grey.400" />
                        <Box2 title="Proposé" text={show(p.field, p.new_value)} color="success.main" />
                      </Stack>
                      {p.stale && (
                        <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5, whiteSpace: 'pre-wrap' }}>
                          Texte vu par le relecteur : {show(p.field, p.base_value)}
                        </Typography>
                      )}
                      {p.note && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>Précision : {p.note}</Typography>}
                    </Box>
                    {isSuperAdmin && status === 'nouveau' && (
                      <Stack spacing={0.5}>
                        <Button size="small" variant="contained" disabled={busy || p.missing} onClick={() => accept([p.id])}>Accepter</Button>
                        <Button size="small" color="error" disabled={busy} onClick={() => refuse([p.id])}>Refuser</Button>
                      </Stack>
                    )}
                  </Paper>
                ))}
              </Stack>
            )}

            {total > PAGE_SIZE && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Pagination count={Math.ceil(total / PAGE_SIZE)} page={page} onChange={(_, v) => setPage(v)} />
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {tab === 1 && <Suggestions part="corrections" />}
      {tab === 2 && <Suggestions part="words" />}

      {tab === 4 && <ExcelTab isSuperAdmin={isSuperAdmin} />}

      {tab === 3 && (
        <Card>
          <CardContent>
            {loadingChanges ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
            ) : changes.length === 0 ? (
              <Typography color="text.secondary">Aucune modification enregistrée pour le moment.</Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Mot</TableCell>
                      <TableCell>Champ</TableCell>
                      <TableCell>Avant</TableCell>
                      <TableCell>Après</TableCell>
                      <TableCell>Par</TableCell>
                      {isSuperAdmin && <TableCell align="center">Action</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {changes.map((c) => (
                      <TableRow key={c.id} sx={{ opacity: c.reverted_at ? 0.55 : 1 }}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(c.created_at).toLocaleString('fr-FR')}</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontStyle: 'italic' }}>{c.duala}</TableCell>
                        <TableCell>{FIELD_LABEL[c.field] || c.field}</TableCell>
                        <TableCell sx={{ maxWidth: 240, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{show(c.field, c.old_value)}</TableCell>
                        <TableCell sx={{ maxWidth: 240, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{show(c.field, c.new_value)}</TableCell>
                        <TableCell>
                          {personName(c.author)}
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{SOURCE_LABEL[c.source] || c.source}</Typography>
                        </TableCell>
                        {isSuperAdmin && (
                          <TableCell align="center">
                            {c.reverted_at ? <Chip size="small" label="Annulée" /> : (c.source === 'annulation' || c.field === 'creation') ? null : (
                              <Button size="small" onClick={() => revert(c)}>Annuler</Button>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {changesTotal > PAGE_SIZE && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Pagination count={Math.ceil(changesTotal / PAGE_SIZE)} page={changesPage} onChange={(_, v) => setChangesPage(v)} />
              </Box>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

export default Validation;
